import type { PersonalCredential } from "@agentry/api-client";
import { useState } from "react";
import { Link } from "react-router";
import { useApi } from "../../app/ApiProvider";
import { useAuth, useScope } from "../../app/AuthProvider";
import { useAction, useAsync } from "../../app/useAsync";
import {
	Badge,
	Button,
	Callout,
	Card,
	DefinitionList,
	Field,
	LinkButton,
	Metric,
	PageHeader,
	Select,
	StatusBadge,
	Table,
	TextInput,
	Toggle,
} from "../../ui/primitives";
import { EmptyState, ErrorState, LoadingState, recoveryFor } from "../../ui/states";
import { formatDateTime } from "./ProgressPages";

export function UserSettingsPage() {
	const { session } = useAuth();
	const { mode, contract, pin } = useApi();
	const [notifyApproval, setNotifyApproval] = useState(true);
	const [notifyFinish, setNotifyFinish] = useState(true);

	return (
		<>
			<PageHeader eyebrow="設定" title="ユーザー設定" description="あなた個人の設定です。" />
			<div className="grid gap-4 lg:grid-cols-2">
				<Card title="プロフィール">
					<DefinitionList
						columns={1}
						items={[
							{ label: "名前", value: session?.user.name ?? "—" },
							{ label: "メールアドレス", value: session?.user.email ?? "—" },
							{ label: "所属", value: session?.tenant.name ?? "—" },
							{ label: "権限", value: roleLabel(session?.membership.role) },
						]}
					/>
					<p className="mt-3 text-[12px] text-ink-500">
						名前とメールアドレスの変更は、組織の管理者にご依頼ください。
					</p>
				</Card>

				<Card title="通知">
					<div className="space-y-2">
						<Toggle
							checked={notifyApproval}
							onChange={setNotifyApproval}
							label="承認が必要になったら通知する"
						/>
						<Toggle
							checked={notifyFinish}
							onChange={setNotifyFinish}
							label="作業が完了したら通知する"
						/>
					</div>
					<p className="mt-3 text-[12px] text-ink-500">
						POCでは設定値の保存先がBackendに未実装のため、この画面内でのみ保持します。
					</p>
				</Card>

				<Card title="AI接続" description="自分のAI契約を使う設定">
					<p className="text-[13.5px] text-ink-700">
						個人のAPI KeyやSubscriptionを登録すると、あなたの依頼で優先的に使われます。
					</p>
					<LinkButton to="/app/settings/credentials" variant="secondary" size="sm" className="mt-3">
						AI接続設定を開く
					</LinkButton>
				</Card>

				<Card title="接続状態" description="このアプリがどこに繋がっているか">
					<DefinitionList
						columns={1}
						items={[
							{
								label: "データ元",
								value: mode === "mock" ? "Mock（Backend未接続）" : "Zerovia Backend",
							},
							{
								label: "Contract",
								value:
									contract.status === "ok"
										? `v${contract.contractVersion}`
										: contract.status === "error"
											? contract.kind === "MISMATCH"
												? "不一致（接続停止）"
												: "接続失敗"
											: "確認中",
							},
							{
								label: "Pin",
								value: `${pin.contractVersion} / ${pin.contractHash.slice(0, 12)}…`,
								mono: true,
							},
						]}
					/>
				</Card>
			</div>
		</>
	);
}

function roleLabel(role: string | undefined): string {
	switch (role) {
		case "tenant_member":
			return "利用者";
		case "tenant_admin":
			return "組織管理者";
		case "platform_admin":
			return "製品管理者";
		case "platform_viewer":
			return "製品参照";
		default:
			return "—";
	}
}

/**
 * 個人API Key / Subscription認証（BYOK / BYOS）。
 *
 * Secret本文は送らず保存もしない。参照（env: / vault:）のみを扱い、
 * 登録後は末尾ヒント以外を再表示しない。
 */
export function CredentialSettingsPage() {
	const { client } = useApi();
	const scope = useScope();
	const [reloadSeq, setReloadSeq] = useState(0);
	const [form, setForm] = useState({
		providerId: "provider-anthropic",
		displayName: "",
		authMethod: "API_KEY" as PersonalCredential["authMethod"],
		secretRef: "",
	});

	const data = useAsync(async () => {
		const [credentials, policy, providers, authRoutes] = await Promise.all([
			client.listPersonalCredentials(scope),
			client.getByoPolicy(scope),
			client.listProviders(scope),
			client.backingOf("getSessionAuthRoutes") === "ZEROVIA"
				? client.getSessionAuthRoutes(scope, "settings")
				: Promise.resolve([]),
		]);
		return { credentials, policy, providers, authRoutes };
	}, [client, scope.tenantId, scope.projectId, reloadSeq]);

	const create = useAction(async () => client.createPersonalCredential(scope, form));
	const test = useAction(async (id: string) => client.testPersonalCredential(scope, id));
	const revoke = useAction(async (id: string) => client.revokePersonalCredential(scope, id));

	if (data.status === "loading") return <LoadingState />;
	if (data.status === "error") return <ErrorState error={data.error} onRetry={data.reload} />;

	const { credentials, policy, providers, authRoutes } = data.data;
	const allowedProviders = providers.items.filter((p) => policy.allowedProviderIds.includes(p.id));
	const methodAllowed = form.authMethod === "API_KEY" ? policy.byokAllowed : policy.byosAllowed;
	const sharedConnections = authRoutes.flatMap((route) =>
		route.candidates
			.filter((candidate) => candidate.credentialClass.startsWith("SYSTEM_"))
			.map((candidate) => ({ ...candidate, providerLabel: route.providerLabel })),
	);

	return (
		<>
			<PageHeader
				eyebrow="AI接続設定"
				title="自分のAI契約を使う"
				description="登録した接続は、あなたの依頼で優先的に使われます。使えない場合は共有の接続に切り替わります。"
			/>

			<Callout tone="warning" title="秘密情報そのものは保存しません">
				API Key、Cookie、Tokenの本文は送信も保存もしません。
				<span className="font-mono"> env: </span>または<span className="font-mono"> vault: </span>
				形式の参照だけを登録してください。登録後に本文を再表示することはありません。
			</Callout>

			<div className="mt-5 grid gap-4 lg:grid-cols-[1fr_360px]">
				<div className="space-y-4">
					<Card title="組織の共有接続">
						{sharedConnections.length === 0 ? (
							<p className="text-[13px] text-ink-500">利用可能な共有接続はありません。</p>
						) : (
							<ul className="space-y-2">
								{sharedConnections.map((connection) => (
									<li
										key={`${connection.providerLabel}:${connection.poolAlias}`}
										className="flex items-center justify-between rounded-lg border border-line-200 px-3 py-2"
									>
										<div>
											<p className="font-semibold text-ink-900">{connection.poolAlias}</p>
											<p className="text-[12px] text-ink-500">{connection.providerLabel}</p>
										</div>
										<Badge tone="success">利用可能</Badge>
									</li>
								))}
							</ul>
						)}
					</Card>

					<Card title="登録済みの接続">
						{credentials.length === 0 ? (
							<EmptyState
								title="まだ登録がありません"
								description="登録しなくても、組織で許可された共有の接続で依頼できます。"
							/>
						) : (
							<Table head={["接続", "方式", "状態", "最終確認", ""]}>
								{credentials.map((credential) => (
									<tr key={credential.id}>
										<td>
											<p className="font-semibold text-ink-900">{credential.displayName}</p>
											<p className="text-[12px] text-ink-500">{credential.providerLabel}</p>
											<p className="font-mono text-[11px] text-ink-400">{credential.maskedHint}</p>
										</td>
										<td className="text-ink-600">
											{credential.authMethod === "API_KEY" ? "API Key" : "Subscription"}
										</td>
										<td>
											<StatusBadge status={credential.status} />
											{credential.lastTestReason && (
												<p className="mt-0.5 font-mono text-[11px] text-danger-600">
													{credential.lastTestReason}
												</p>
											)}
										</td>
										<td className="text-ink-600">{formatDateTime(credential.lastVerifiedAt)}</td>
										<td>
											<div className="flex gap-1.5">
												<Button
													size="sm"
													variant="secondary"
													disabled={test.pending}
													onClick={async () => {
														await test.run(credential.id);
														setReloadSeq((n) => n + 1);
													}}
												>
													接続Test
												</Button>
												<Button
													size="sm"
													variant="danger"
													disabled={revoke.pending}
													onClick={async () => {
														await revoke.run(credential.id);
														setReloadSeq((n) => n + 1);
													}}
												>
													削除
												</Button>
											</div>
										</td>
									</tr>
								))}
							</Table>
						)}

						{test.result && (
							<div className="mt-3">
								<Callout
									tone={test.result.result === "SUCCESS" ? "success" : "danger"}
									title={test.result.result === "SUCCESS" ? "接続できました" : "接続に失敗しました"}
								>
									{test.result.result === "SUCCESS" ? (
										<>確認日時: {formatDateTime(test.result.verifiedAt)}</>
									) : (
										<>
											<p className="mt-1">原因: {test.result.reason}</p>
											<p className="mt-1">再認証が必要です。削除して登録し直してください。</p>
										</>
									)}
									<p className="mt-1 text-[12px]">
										接続Testの結果に、秘密情報やログインIDは表示しません。
									</p>
								</Callout>
							</div>
						)}
					</Card>
				</div>

				<div className="space-y-4">
					<Card title="組織の許可状況">
						<DefinitionList
							columns={1}
							items={[
								{
									label: "個人API Key（BYOK）",
									value: (
										<Badge tone={policy.byokAllowed ? "success" : "danger"}>
											{policy.byokAllowed ? "許可" : "不可"}
										</Badge>
									),
								},
								{
									label: "個人Subscription（BYOS）",
									value: (
										<Badge tone={policy.byosAllowed ? "success" : "danger"}>
											{policy.byosAllowed ? "許可" : "不可"}
										</Badge>
									),
								},
							]}
						/>
						{policy.note && <p className="mt-2 text-[12.5px] text-ink-500">{policy.note}</p>}
					</Card>

					<Card title="接続を追加する">
						<form
							className="space-y-3"
							onSubmit={async (event) => {
								event.preventDefault();
								const created = await create.run();
								if (created) {
									setForm({ ...form, displayName: "", secretRef: "" });
									setReloadSeq((n) => n + 1);
								}
							}}
						>
							<Field label="AI提供元">
								<Select
									value={form.providerId}
									onChange={(event) => setForm({ ...form, providerId: event.target.value })}
								>
									{allowedProviders.map((provider) => (
										<option key={provider.id} value={provider.id}>
											{provider.displayName}
										</option>
									))}
								</Select>
							</Field>
							<Field label="接続方式">
								<Select
									value={form.authMethod}
									onChange={(event) =>
										setForm({
											...form,
											authMethod: event.target.value as PersonalCredential["authMethod"],
										})
									}
								>
									<option value="API_KEY">個人API Key</option>
									<option value="SUBSCRIPTION_LOGIN">個人Subscriptionログイン</option>
								</Select>
							</Field>
							<Field label="表示名">
								<TextInput
									value={form.displayName}
									onChange={(event) => setForm({ ...form, displayName: event.target.value })}
									placeholder="例: 個人Claude Subscription"
									required
								/>
							</Field>
							<Field
								label="参照"
								hint="env:MY_KEY_NAME または vault:path/to/secret の形式。Key本文は入力できません。"
							>
								<TextInput
									value={form.secretRef}
									onChange={(event) => setForm({ ...form, secretRef: event.target.value })}
									placeholder="vault:user-01/anthropic/session"
									required
								/>
							</Field>

							{create.error != null && (
								<div
									role="alert"
									className="rounded-lg border border-danger-500/40 bg-danger-100 px-3 py-2"
								>
									<p className="text-[13px] font-bold text-danger-600">
										{recoveryFor(create.error).title}
									</p>
									<p className="text-[12.5px] text-ink-700">{recoveryFor(create.error).hint}</p>
								</div>
							)}

							<Button type="submit" className="w-full" disabled={create.pending || !methodAllowed}>
								{create.pending ? "登録しています…" : "登録する"}
							</Button>
							{!methodAllowed && (
								<p className="text-[12px] text-ink-500">
									この方式は組織で許可されていません。管理者にご相談ください。
								</p>
							)}
						</form>
					</Card>
				</div>
			</div>
		</>
	);
}

export function UsagePage() {
	const { client } = useApi();
	const scope = useScope();
	const data = useAsync(async () => {
		const [usage, costs] = await Promise.all([
			client.getUsageSummary(scope),
			client.listCostEvents(scope, { limit: 50 }),
		]);
		return { usage, costs };
	}, [client, scope.tenantId, scope.projectId]);

	return (
		<>
			<PageHeader
				eyebrow="利用状況"
				title="どれくらい使いましたか"
				description="実費として取得できた分と、取得できない分を分けて表示します。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<div className="space-y-4">
					<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
						<Metric label="実行数" value={data.data.usage.executions} unit="件" />
						<Metric
							label="完了"
							value={data.data.usage.succeededExecutions}
							unit="件"
							hint={`成功率 ${data.data.usage.executions === 0 ? 0 : Math.round((data.data.usage.succeededExecutions / data.data.usage.executions) * 100)}%`}
						/>
						<Metric label="成果物" value={data.data.usage.artifacts} unit="件" />
						<Metric
							label="費用"
							value={`¥${Math.round(data.data.usage.amount).toLocaleString()}`}
							hint="実費として取得できた分のみ"
						/>
					</div>

					<Card title="費用の内訳" description="計測できた種類ごとに分けています">
						<Table head={["計測区分", "件数", "金額"]}>
							{data.data.usage.byMeteringMode.map((row) => (
								<tr key={row.mode}>
									<td>{meteringLabel(row.mode)}</td>
									<td className="text-ink-600">{row.executions}</td>
									<td className={row.amount === null ? "text-ink-400" : "text-ink-900"}>
										{row.amount === null
											? "取得不能"
											: `¥${Math.round(row.amount).toLocaleString()}`}
									</td>
								</tr>
							))}
						</Table>
						<p className="mt-2 text-[12px] text-ink-500">
							「取得不能」は0円ではありません。Provider側から金額・使用量を取得できなかった実行です。
						</p>
					</Card>

					<Card title="最近の実行">
						<Table head={["実行", "AI", "入力", "出力", "金額", "日時"]}>
							{data.data.costs.items.map((cost) => (
								<tr key={cost.id}>
									<td>
										{cost.executionId ? (
											<Link
												to={`/app/progress/${cost.executionId}`}
												className="font-mono text-[12px] text-brand-600 hover:underline"
											>
												{cost.executionId}
											</Link>
										) : (
											<span className="text-ink-400">—</span>
										)}
									</td>
									<td className="text-ink-600">{cost.providerId}</td>
									<td className="text-ink-600">{cost.inputTokens.toLocaleString()}</td>
									<td className="text-ink-600">{cost.outputTokens.toLocaleString()}</td>
									<td className={cost.amount === null ? "text-ink-400" : "text-ink-900"}>
										{cost.amount === null ? "取得不能" : `¥${cost.amount.toLocaleString()}`}
									</td>
									<td className="text-ink-600">{formatDateTime(cost.createdAt)}</td>
								</tr>
							))}
						</Table>
					</Card>
				</div>
			)}
		</>
	);
}

function meteringLabel(mode: string): string {
	switch (mode) {
		case "ACTUAL_MONEY":
			return "実際の金額";
		case "ACTUAL_USAGE":
			return "実際の使用量";
		case "ESTIMATED_OPPORTUNITY_COST":
			return "推定（機会費用）";
		case "UNAVAILABLE":
			return "取得不能";
		default:
			return mode;
	}
}

export function NotificationsPage() {
	const { client } = useApi();
	const scope = useScope();
	const [reloadSeq, setReloadSeq] = useState(0);
	const data = useAsync(
		() => client.listNotifications(scope),
		[client, scope.tenantId, scope.projectId, reloadSeq],
	);
	const markRead = useAction(async (id: string) => client.markNotificationRead(scope, id));

	return (
		<>
			<PageHeader
				eyebrow="お知らせ"
				title="通知"
				description="対応が必要なものが上に表示されます。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" &&
				(data.data.length === 0 ? (
					<EmptyState title="通知はありません" />
				) : (
					<ul className="space-y-2">
						{[...data.data]
							.sort((a, b) => Number(a.read) - Number(b.read))
							.map((notification) => (
								<li
									key={notification.id}
									className={
										notification.read
											? "ag-card px-4 py-3 opacity-70"
											: "ag-card border-brand-300 px-4 py-3"
									}
								>
									<div className="flex flex-wrap items-start justify-between gap-3">
										<div className="min-w-0">
											<p className="text-[14.5px] font-semibold text-ink-900">
												{notification.title}
											</p>
											<p className="mt-0.5 text-[13px] text-ink-600">{notification.body}</p>
											<p className="mt-1 text-[12px] text-ink-400">
												{formatDateTime(notification.createdAt)}
											</p>
										</div>
										<div className="flex shrink-0 gap-2">
											{notification.link && (
												<LinkButton to={notification.link} variant="secondary" size="sm">
													開く
												</LinkButton>
											)}
											{!notification.read && (
												<Button
													size="sm"
													variant="ghost"
													onClick={async () => {
														await markRead.run(notification.id);
														setReloadSeq((n) => n + 1);
													}}
												>
													既読にする
												</Button>
											)}
										</div>
									</div>
								</li>
							))}
					</ul>
				))}
		</>
	);
}
